import { DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WorkflowStep, WorkflowStepInterface, WorkflowStepOutputParameterContainer } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AutomationGraphUtilitiesService } from '../../../automation-graph-utilities.service';
import { WorkflowStepRulesFilterComponent } from '../workflow-step-rules-filter/workflow-step-rules-filter.component';

export interface StepFilterComponentInterface {
  getWorkflowStep(): WorkflowStep;

  getTaskDefinitionId(): string;

  // getControlsToValidateOnSave should return all controls to be touched and updated for validation display purposes
  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[];

  isFormValid(): boolean;

  /** Emits true if the filter is loading contents, false otherwise.*/
  isLoading(): Observable<boolean>;
}

@Component({
  selector: 'automata-workflow-step-filter',
  templateUrl: './workflow-step-filter.component.html',
  styleUrls: ['./workflow-step-filter.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    GalaxyTooltipModule,
    WorkflowStepRulesFilterComponent,
    DragDropModule,
  ],
})
export class WorkflowStepFilterComponent
  implements StepFilterComponentInterface, OnChanges, OnInit, OnDestroy, AfterViewInit
{
  @Input() filterStep: WorkflowStepInterface;
  @Input() predecessorOutputParams: WorkflowStepOutputParameterContainer[];
  @Input() showClose = false;
  @Input() displayInternalFilters = false;
  @Input() allowMultiBranching = false;
  @Input() selectedTaskDefinitionId: string;
  @Input() isGoal = false;

  // This is only emitted if allowMutliBranching is false. If allowMultiBranching is true then this component can
  // manage it's own removal
  @Output() removed = new EventEmitter();

  @ViewChildren(WorkflowStepRulesFilterComponent) rulesFilterComponents!: QueryList<WorkflowStepRulesFilterComponent>;
  rulesFilterComponentsList$$: BehaviorSubject<QueryList<WorkflowStepRulesFilterComponent>> = new BehaviorSubject<
    QueryList<WorkflowStepRulesFilterComponent>
  >(null);
  filterStepCopy: WorkflowStepInterface;
  commonMergeNode: string;
  destroyed$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  data: { [key: string]: string } = {};

  constructor(
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
    private readonly graphUtilities: AutomationGraphUtilitiesService,
  ) {}

  ngOnInit(): void {
    this.filterStepCopy = JSON.parse(JSON.stringify(this.filterStep));
    if (this.filterStep.id) {
      this.commonMergeNode = this.graphUtilities.getMergeStep(this.filterStep.id);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.filterStep?.currentValue) {
      this.filterStepCopy = changes.filterStep.currentValue;
      if (changes.filterStep.currentValue.id) {
        this.commonMergeNode = this.graphUtilities.getMergeStep(changes.filterStep.currentValue.id);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$$.next(true);
  }

  ngAfterViewInit(): void {
    //The query list is populated before this lifecycle hook so we need to set the initial value, then rely on changes
    this.rulesFilterComponentsList$$.next(this.rulesFilterComponents);
    this.rulesFilterComponents.changes
      .pipe(takeUntil(this.destroyed$$))
      .subscribe((changes) => this.rulesFilterComponentsList$$.next(changes));
  }

  isLoading(): Observable<boolean> {
    return this.rulesFilterComponentsList$$.pipe(
      map((components) => components || []),
      switchMap((components: WorkflowStepRulesFilterComponent[]) => {
        const filteredComponents = components.filter((c) => !!c) as WorkflowStepRulesFilterComponent[];

        if (filteredComponents.length > 0) {
          const isLoadingObservables = filteredComponents.map((c) => c.isLoading$);
          return combineLatest(isLoadingObservables).pipe(
            map((isLoadingValues) => {
              const isLoading = isLoadingValues.reduce((acc, curr) => acc && curr, true);
              return !!isLoading;
            }),
          );
        }

        return of(false);
      }),
    );
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return this.rulesFilterComponents.reduce((prev, cur) => {
      prev.push(...cur.getControlsToValidateOnSave());
      return prev;
    }, []);
  }

  isFormValid(): boolean {
    return this.rulesFilterComponents.reduce((prev, cur) => {
      return prev && cur.isFormValid();
    }, true as boolean);
  }

  getWorkflowStep(): WorkflowStep {
    const rules = this.rulesFilterComponents.map((c) => c.getRule());
    return new WorkflowStep({
      id: this.filterStep?.id || uuidv4(),
      taskDefinitionId: this.filterStepCopy.taskDefinitionId,
      data: JSON.stringify(this.data),
      choices: {
        rules: rules,
      },
    });
  }

  setNewTaskDefinitionId(taskDefinitionId: string, index: number): void {
    if (index === 0) {
      this.filterStepCopy.taskDefinitionId = taskDefinitionId;
    }
  }

  setStepData($event: { [key: string]: string }): void {
    this.data = $event;
  }

  getTaskDefinitionId(): string {
    return this.filterStepCopy.taskDefinitionId;
  }

  addBranch(): void {
    this.filterStepCopy.choices.rules.push(null);
  }

  remove(index: number): void {
    if (this.allowMultiBranching) {
      // if we allow multi branching then this component is in charge of removing the rule. If we don't then the parent
      // component is responsible for doing the removal.
      // This is because multi branching = multiple rules, non multi branching = multiple steps
      if (this.filterStepCopy.choices.rules[index]?.next) {
        // We cannot delete a rule that is the only thing that points to a step. If the rule points to a merge step
        // then another rule must be pointing to it and we don't have to worry about orphaning that step.
        if (!this.graphUtilities.isAMergeStep(this.filterStepCopy.choices.rules[index]?.next)) {
          this.snackbarService.errorSnack(
            this.translateService.instant('AUTOMATIONS.EDITOR.ERRORS.CANT_DELETE_BRANCH_V2'),
          );
          return;
        }
      }
      this.filterStepCopy.choices.rules.splice(index, 1);
    } else {
      this.removed.emit();
    }
  }

  drop(event: any): void {
    moveItemInArray(this.filterStepCopy?.choices?.rules, event.previousIndex, event.currentIndex);
  }
}
